// Subscription management mixin

import { markRaw } from 'vue';
export default {
    data() {
        return {
            // UI controls
            show_pricing_modal: false,
            pricing_loading: false,
            subscription_loading: false,
            pricing_config_loading: false,
            billing_cycle: 'yearly', // Default to yearly for better pricing psychology
            schedule_key: 'subscription.cancel_downgrade', //!this is only for the top menu user, NOT the tiers
            show_refresh_modal: false, // Modal to force page refresh after critical subscription actions

            // Feature controls 
            show_ai_features: true, // Show Creator Pro tier (AI features marked as "coming soon")
            show_credit_packs: false, // Hide credit packs until AI system is ready
            ai_system_enabled: false,

            // Server data (loaded from subscription_api.php)
            pricing_config: null,
            tier_limits: null,
            credit_packs: null,
            pricing_tiers: [],
            tier_config: null, // Centralized tier configuration from server (includes hierarchy)
            user_billing_info: null // Current user billing cycle info from server (tier, billing_cycle, product_id)
        };
    },

    computed: {
        // Current user tier from user_data
        current_user_tier() {
            if (!this.user_data || !this.user_data.tier) {
                return this.tier_config ? this.tier_config.names.free : 'free';
            }
            return this.user_data.tier;
        },

        // Current user tier with billing cycle (e.g. 'premium' becomes 'premium_yearly' if user has yearly plan)
        current_user_tier_with_cycle() {
            if (!this.user_billing_info) {
                return this.current_user_tier;
            }

            const base_tier = this.current_user_tier;
            const billing_cycle = this.user_billing_info.billing_cycle;

            // If user has yearly billing and not free tier, append _yearly to tier
            if (billing_cycle === 'yearly' && base_tier !== 'free') {
                return base_tier + '_yearly';
            }

            return base_tier;
        },

        // Filter tiers based on feature flags and load from server
        available_tiers() {
            if (!this.pricing_config || !this.pricing_config.pricing_tiers || !this.tier_limits) {
                return [];
            }

            const current_tier = this.current_user_tier_with_cycle; // Use tier with billing cycle info

            // Show tiers based on selected billing cycle
            const tiers_to_show = this.pricing_config.pricing_tiers.filter(tier => {
                if (tier.id === 'free') return true; // Always show free

                if (this.billing_cycle === 'yearly') {
                    // Show yearly tiers (premium_yearly, creator_pro_yearly)
                    return tier.id.includes('_yearly');
                } else {
                    // Show monthly tiers (premium, creator_pro) 
                    return !tier.id.includes('_yearly');
                }
            });

            const tiers = tiers_to_show.map(tier => {
                // Create translated version of each tier
                const translated_tier = { ...tier };

                // Set actual_id for API calls (no manipulation needed now)
                translated_tier.actual_id = tier.id;

                // Adjust display for yearly tiers
                if (tier.id.includes('_yearly')) {
                    translated_tier.price = (tier.price / 12).toFixed(2);
                    translated_tier.period = this.get_t('pricing.month');
                    translated_tier.yearly_total = tier.price;
                } else if (tier.id !== 'free') {
                    translated_tier.period = this.get_t('pricing.month');
                }

                // Add translated content based on base tier
                const base_tier = tier.id.replace('_yearly', '');

                if (base_tier === 'free') {
                    translated_tier.name = this.get_t('pricing.free.name');
                    translated_tier.description = this.get_t('pricing.free.description');
                    const limits = this.tier_limits?.free || {};

                    // Reorganized: Credits first, separator, then Mikomic features
                    translated_tier.features = [
                        { text: '0', enabled: true, display_type: 'credits' },
                        { text: this.get_t('pricing.mikomic_features_separator'), enabled: true, display_type: 'separator' },
                        { text: this.get_t('pricing.free.feature_export', { width: limits.max_export_width, height: limits.max_export_height }), enabled: true },
                        { text: this.get_t('pricing.free.feature_panels', { max: limits.max_panels_per_page }), enabled: true },
                        { text: this.get_t('pricing.free.feature_balloons', { max: limits.max_balloons_per_page }), enabled: true },
                        { text: this.get_t('pricing.free.feature_layers', { max: limits.max_layers_per_panel }), enabled: true },
                        { text: this.get_t('pricing.free.feature_undo', { steps: limits.undo_steps }), enabled: true },
                        { text: this.get_t('pricing.free.feature_autosave', { interval: limits.autosave_interval }), enabled: true },
                        { text: this.get_t('pricing.free.feature_pages', { max: limits.max_pages_per_chapter }), enabled: true },
                        { text: this.get_t('pricing.free.feature_generation_history', { count: limits.image_gen_history_count || 10 }), enabled: true }
                    ];

                    translated_tier.limitations = [
                        this.get_t('pricing.free.limitation_2'),
                        this.get_t('pricing.free.limitation_3')
                    ].map(text => ({ text, enabled: false }));
                } else if (base_tier === 'premium') {
                    translated_tier.name = this.get_t('pricing.premium.name');
                    translated_tier.description = this.get_t('pricing.premium.description');
                    const limits = this.tier_limits?.premium || {};

                    // Reorganized: Credits first, separator, then Mikomic features
                    translated_tier.features = [
                        { text: String(limits.ai_tokens_monthly || 200), enabled: true, display_type: 'credits' },
                        { text: this.get_t('pricing.mikomic_features_separator'), enabled: true, display_type: 'separator' },
                        { text: this.get_t('pricing.premium.feature_export', { width: limits.max_export_width, height: limits.max_export_height }), enabled: true },
                        { text: this.get_t('pricing.premium.feature_unlimited'), enabled: true },
                        { text: this.get_t('pricing.premium.feature_layers'), enabled: true },
                        { text: this.get_t('pricing.premium.feature_copy_styles'), enabled: true },
                        { text: this.get_t('pricing.premium.feature_undo', { steps: limits.undo_steps }), enabled: true },
                        { text: this.get_t('pricing.premium.feature_autosave', { interval: limits.autosave_interval }), enabled: true },
                        { text: this.get_t('pricing.premium.feature_pages', { max: limits.max_pages_per_chapter }), enabled: true },
                        { text: this.get_t('pricing.premium.feature_generation_history', { count: limits.image_gen_history_count || 100 }), enabled: true },
                        { text: this.get_t('pricing.premium.feature_gallery'), enabled: true },
                        { text: this.get_t('pricing.premium.feature_support'), enabled: true }
                    ];
                } else if (base_tier === 'creator_pro') {
                    translated_tier.name = this.get_t('pricing.creator_pro.name');
                    translated_tier.description = this.get_t('pricing.creator_pro.description');
                    const limits = this.tier_limits?.creator_pro || {};

                    // Reorganized: Credits first, separator, then Mikomic features
                    translated_tier.features = [
                        { text: String(limits.ai_tokens_monthly || 500), enabled: true, display_type: 'credits' },
                        { text: this.get_t('pricing.mikomic_features_separator'), enabled: true, display_type: 'separator' },
                        { text: this.get_t('pricing.creator_pro.feature_export', { width: limits.max_export_width || 10000, height: limits.max_export_height || 10000 }), enabled: true },
                        { text: this.get_t('pricing.creator_pro.feature_pages', { max: limits.max_pages_per_chapter }), enabled: true },
                        { text: this.get_t('pricing.creator_pro.feature_generation_history', { count: limits.image_gen_history_count || 500 }), enabled: true },
                        //{ text: this.get_t('pricing.creator_pro.feature_ai_generation'), enabled: this.feature_flags?.ai_generation || true },
                        { text: this.get_t('pricing.creator_pro.feature_character_manager'), enabled: this.feature_flags?.ai_character_manager || false },
                        { text: this.get_t('pricing.creator_pro.feature_translation'), enabled: this.feature_flags?.ai_translation || false },
                        { text: this.get_t('pricing.creator_pro.feature_all_premium'), enabled: true }
                    ];
                }

                // Use server-calculated values only (now correctly matched)
                translated_tier.is_current = tier.is_current || false;
                translated_tier.cta_text = tier.button_text;
                translated_tier.disabled = tier.button_disabled !== undefined ? tier.button_disabled : false;

                return translated_tier;
            });

            if (this.show_ai_features) {
                return tiers;
            } else {
                // Hide Creator Pro tier until AI features are ready
                const creator_pro_tier = this.tier_config ? this.tier_config.names.creator_pro : 'creator_pro';
                return tiers.filter(tier => tier.id !== creator_pro_tier);
            }
        },

        // Translated credit packs from server
        translated_credit_packs() {
            if (!this.pricing_config || !this.pricing_config.ai_credit_packs) {
                return [];
            }

            return Object.keys(this.pricing_config.ai_credit_packs).map(key => {
                const pack = this.pricing_config.ai_credit_packs[key];
                return {
                    id: key,
                    name: this.get_t(`credits.${key}.name`),
                    description: this.get_t(`credits.${key}.description`),
                    credits: pack.credits,
                    price: pack.price_eur,
                    currency: '€',
                    stripe_price_id: `credits_${key}`,
                    popular: key === 'power' // Mark power pack as popular
                };
            });
        },

        // Check if user can access premium features
        is_premium_user() {
            if (!this.tier_config) return false;

            const premium_tier = this.tier_config.names.premium;
            const creator_pro_tier = this.tier_config.names.creator_pro;

            return this.user_data.tier === premium_tier || this.user_data.tier === creator_pro_tier;
        },

        // Check if user can access AI features
        is_creator_pro_user() {
            if (!this.tier_config) return false;

            const creator_pro_tier = this.tier_config.names.creator_pro;

            return this.user_data.tier === creator_pro_tier;
        }
    },

    methods: {
        // Load pricing configuration from server
        async load_pricing_config() {
            if (this.pricing_config_loading) return;

            this.pricing_config_loading = true;

            try {
                const formData = new FormData();
                formData.append('request', 'get_pricing_config');

                const response = await axios.post('./subscription_api.php', formData);

                if (response.data.success) {
                    //console.log("data", response.data);
                    this.pricing_config = response.data.data.pricing_config ? markRaw(Object.freeze(response.data.data.pricing_config)) : null;
                    this.tier_limits = response.data.data.tier_limits ? markRaw(Object.freeze(response.data.data.tier_limits)) : null;
                    this.credit_packs = response.data.data.credit_packs ? markRaw(Object.freeze(response.data.data.credit_packs)) : null;
                    this.pricing_tiers = response.data.data.pricing_config?.pricing_tiers ? markRaw(Object.freeze(response.data.data.pricing_config.pricing_tiers)) : null;
                    this.tier_config = response.data.data.tier_config ? markRaw(Object.freeze(response.data.data.tier_config)) : null; // Includes hierarchy
                    
                    this.show_credit_packs = response.data.data.pricing_config.features.credits_enabled;
                    this.ai_system_enabled = response.data.data.pricing_config.features.ai_enabled;
                    this.schedule_key = response.data.schedule_key;
                } else {
                    console.error('Failed to load pricing config:', response.data.message);
                }

            } catch (error) {
                console.error('Error loading pricing config:', error);
            } finally {
                this.pricing_config_loading = false;
            }
        },

        // Initialize subscription data
        init_subscription_data() {
            // Check if user returned from successful payment
            this.check_payment_success();

            // Load pricing config from server
            this.load_pricing_config();
        },

        // Check for payment success URL parameter and show modal
        check_payment_success() {
            const url_params = new URLSearchParams(window.location.search);
            if (url_params.get('payment') === 'success') {
                // Wait for language to load if it's still loading
                if (this.language_loading) {
                    const check_language_loaded = () => {
                        if (!this.language_loading) {
                            this.show_payment_success_modal();
                        } else {
                            setTimeout(check_language_loaded, 100);
                        }
                    };
                    check_language_loaded();
                } else {
                    this.show_payment_success_modal();
                }
            }
        },

        show_payment_success_modal() {
            this.gtag_report_conversion();

            // Show success modal with proper translation
            const success_message = this.get_t('pricing.payment_success');

            this.show_subscription_success_modal(success_message, 'success');

            // Clean URL parameters
            const clean_url = window.location.protocol + "//" + window.location.host + window.location.pathname;
            window.history.replaceState({}, document.title, clean_url);
        },

        // Show pricing modal
        async show_pricing() {
            this.show_pricing_modal = true;            
            await this.get_billing_info();

            if (this.user_billing_info.tier != "free" && this.user_billing_info.billing_cycle == "monthly") {
                this.billing_cycle = "monthly";
            }
        },


        async get_billing_info() {

            try {
                const formData = new FormData();
                formData.append('request', 'get_billing_info');

                const response = await axios.post('./subscription_api.php', formData);

                if (response.data.success) {
                    //console.log("data billing", response.data.billing_info);
                    this.user_billing_info = response.data.billing_info; // User billing cycle info
                } else {
                    console.error('Failed to load billing info:', response.data.message);
                }

            } catch (error) {
                console.error('Error loading billing info:', error);
            }

        },

        // Hide pricing modal
        hide_pricing() {
            this.show_pricing_modal = false;
        },

        // Helper function to determine if tier change is upgrade or downgrade (removed - using server data)

        // Handle subscription upgrade using subscription_api.php
        async upgrade_to_tier(tier_id) {
            if (this.pricing_loading) {
                console.log('Already processing, ignoring click');
                return;
            }

            // Set loading immediately to prevent multiple clicks
            this.pricing_loading = true;

            try {
                // Check if this is a cancel upgrade action (tier is scheduled)
                const tier_info = this.pricing_tiers?.find(t => t.id === tier_id);
                if (tier_info?.is_scheduled) {
                    console.log('Canceling scheduled upgrade for tier:', tier_id);
                    await this.cancel_scheduled_upgrade();
                    return;
                }

                // Check if user has scheduled downgrade and show warning modal
                if (this.user_data && this.user_data.has_scheduled_downgrade) {
                    this.show_schedule_warning_modal(() => {
                        // User confirmed - cancel existing schedule silently and proceed with new action
                        this.cancel_downgrade(true).then((result) => {
                            if (result.success) {
                                // After successful cancellation, proceed with the new tier action
                                this._proceed_with_upgrade(tier_id);
                            }
                        });
                    });
                    return;
                }

                // Check if user already has an active subscription
                if (this.user_data.tier !== 'free' && this.user_data.subscription_status === 'active') {
                    console.log('User has active subscription, showing confirmation');

                    // Determine if upgrade/downgrade using tier hierarchy
                    const current_tier = this.user_data.tier;
                    const target_tier = tier_id.replace('_yearly', ''); // Remove yearly suffix for comparison

                    const tier_hierarchy = this.tier_config?.hierarchy || ['free', 'premium', 'creator_pro'];
                    const current_level = tier_hierarchy.indexOf(current_tier);
                    const target_level = tier_hierarchy.indexOf(target_tier);


                    const is_upgrade = target_level > current_level ||
                        (target_level === current_level && tier_id.includes('_yearly') && this.user_billing_info?.billing_cycle === 'monthly');

                    let confirm_message, confirm_title;

                    if (is_upgrade) {
                        confirm_title = this.get_t('pricing.upgrade_confirm_title');
                        // Get detailed upgrade information from backend
                        this.show_detailed_upgrade_modal(tier_id);
                        return;
                    } else {
                        // Show detailed downgrade modal with clear information
                        this.show_detailed_downgrade_modal(tier_id);
                        return;
                    }

                    // Reset loading since we're showing a confirmation dialog
                    this.pricing_loading = false;
                    return; // Exit here, callbacks will handle the rest
                }

                // Continue with normal flow
                await this._proceed_with_upgrade(tier_id);

            } catch (error) {
                console.error('Tier change error:', error);
                const error_message = error.message || this.get_t('errors.upgrade_failed');
                this.show_notification_modal(error_message, 'error');
            } finally {
                this.pricing_loading = false;
            }
        },

        // Extracted upgrade logic to be reusable
        async _proceed_with_upgrade(tier_id) {
            console.log('Starting upgrade to tier:', tier_id);
            this.pricing_loading = true;

            try {
                const formData = new FormData();
                formData.append('request', 'create_stripe_checkout');
                formData.append('tier', tier_id);

                const response = await axios.post('./subscription_api.php', formData);
                //console.log('Upgrade response:', response.data);

                if (response.data.success) {
                    if (response.data.checkout_url) {
                        // PAYMENT REQUIRED: Redirect to Stripe Checkout
                        console.log('Payment required, redirecting to checkout:', response.data.checkout_url);
                        window.location.href = response.data.checkout_url;
                    } else if (response.data.is_downgrade) {
                        // DOWNGRADE: Show success message with specific downgrade info
                        console.log('Downgrade processed successfully');
                        const success_message = response.data.message || this.get_t('subscription.downgrade_scheduled');
                        this.show_subscription_success_modal(success_message, 'success');

                        // Hide pricing modal
                        this.hide_pricing();
                    } else if (response.data.is_subscription_update) {
                        // SUBSCRIPTION UPDATE: Change processed immediately (no payment needed)
                        console.log('Subscription updated immediately');
                        const success_message = response.data.message;
                        this.show_subscription_success_modal(success_message, 'success');

                        // Hide pricing modal
                        this.hide_pricing();
                    } else {
                        // This should NEVER happen - backend must return clear response type
                        console.error('BACKEND ERROR: Unclear response type');
                        console.error('Response:', response.data);
                        const error_message = 'Server response error - please try again';
                        this.show_notification_modal(error_message, 'error');
                    }
                } else {
                    // Show the actual error message from server
                    console.error('Upgrade failed:', response.data.message);
                    const error_message = response.data.message || this.get_t('errors.upgrade_failed');
                    this.show_notification_modal(error_message, 'error');
                }
            } catch (error) {
                console.error('Upgrade request error:', error);
                const error_message = error.message || this.get_t('errors.upgrade_failed');
                this.show_notification_modal(error_message, 'error');
            } finally {
                // Reset loading state unless redirecting to checkout
                if (!window.location.href.includes('checkout')) {
                    this.pricing_loading = false;
                }
            }
        },

        async show_detailed_upgrade_modal(tier_id) {
            try {
                this.pricing_loading = true;

                // Check if this is an annual to annual upgrade (ONLY case for annual upgrade modal)
                const is_annual_tier = tier_id.includes('_yearly');
                const current_billing_cycle = this.user_billing_info?.billing_cycle || 'monthly';
                const current_tier = this.current_user_tier;
                const target_base_tier = tier_id.replace('_yearly', '');
                const is_annual_to_annual_upgrade = (current_billing_cycle === 'yearly' && is_annual_tier && current_tier !== target_base_tier);

                // console.log('=== DEBUG: Annual upgrade detection ===');
                // console.log('tier_id:', tier_id);
                // console.log('is_annual_tier:', is_annual_tier);
                // console.log('current_billing_cycle:', current_billing_cycle);
                // console.log('current_tier:', current_tier);
                // console.log('target_base_tier:', target_base_tier);
                // console.log('current_tier !== target_base_tier:', current_tier !== target_base_tier);
                // console.log('FINAL is_annual_to_annual_upgrade:', is_annual_to_annual_upgrade);
                // console.log('this.user_billing_info:', this.user_billing_info);
                // console.log('========================================');

                if (is_annual_to_annual_upgrade) {
                    //console.log('SHOWING ANNUAL UPGRADE MODAL');
                    // Show annual upgrade modal ONLY for annual to annual upgrades
                    this.show_annual_upgrade_modal(tier_id);
                    return;
                }
                else {
                    //console.log('SHOWING REGULAR UPGRADE MODAL');
                }

                // Get detailed upgrade information from backend for non-annual upgrades
                const formData = new FormData();
                formData.append('request', 'get_upgrade_details');
                formData.append('tier', tier_id);

                const response = await axios.post('./subscription_api.php', formData);

                if (response.data.success) {
                    const details = response.data;

                    if (details.analysis.action === 'upgrade') {
                        this.show_upgrade_confirmation_modal(details);
                    } else if (details.analysis.action === 'upgrade_special') {
                        this.show_special_upgrade_modal(details);
                    } else {
                        // Fallback to simple confirmation
                        this.show_simple_confirmation(tier_id);
                    }
                } else {
                    console.error('Failed to get upgrade details:', response.data.message);
                    this.show_simple_confirmation(tier_id);
                }
            } catch (error) {
                console.error('Error getting upgrade details:', error);
                this.show_simple_confirmation(tier_id);
            } finally {
                this.pricing_loading = false;
            }
        },

        show_upgrade_confirmation_modal(details) {
            const target_tier_name = details.target.base_tier === 'creator_pro' ?
                this.get_t('pricing.creator_pro.name') : this.get_t('pricing.premium.name');
            const upgrade_calculation = details.upgrade_calculation; // NEW STRUCTURE

            // Debug: Log the details to see what we're getting
            console.log('Upgrade details received:', details);

            if (!upgrade_calculation) {
                console.error('upgrade_calculation is missing from response');
                this.show_simple_confirmation(details.target.tier_id);
                return;
            }

            // Build professional HTML message with modern design
            let html_message = `
                <div class="upgrade-details max-w-md mx-auto">
                    <!-- Header with tier name and icon -->
                    <div class="text-center mb-8">
                        <div class="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl mb-4">
                            <svg class="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path>
                            </svg>
                        </div>
                        <h3 class="text-2xl font-bold text-white mb-2">${this.get_t('pricing.upgrade_header', {
                tier_name: target_tier_name
            })}</h3>
                        <p class="text-gray-400 text-sm">${this.get_t('pricing.upgrade_details_subtitle')}</p>
                    </div>
                    
                    <!-- Price highlight - SHOW UPGRADE PRICE ONLY -->
                    <div class="bg-gradient-to-r from-blue-600/20 to-purple-600/20 border border-blue-500/30 rounded-xl p-6 mb-6">
                        <div class="text-center">
                            <div class="text-3xl font-bold text-white mb-1">€${upgrade_calculation.upgrade_price}</div>
                            <div class="text-blue-300 text-sm font-medium">${this.get_t('pricing.upgrade_price_today')}</div>
                        </div>
                    </div>`;

            // Credits section with modern card design
            if (this.ai_system_enabled) {
                html_message += `
                    <div class="bg-gray-800/50 border border-gray-700/50 rounded-xl p-5 mb-6">
                        <div class="flex items-center mb-4">
                            <div class="w-8 h-8 bg-green-500/20 rounded-lg flex items-center justify-center mr-3">
                                <svg class="w-7 h-7 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"></path>
                                </svg>
                            </div>
                            <h4 class="text-lg font-semibold text-white">${this.get_t('pricing.ai_credits')}</h4>
                        </div>
                        <div class="space-y-3">
                            <div class="flex justify-between items-center">
                                <span class="text-gray-300">${this.get_t('pricing.current_credits')}</span>
                                <span class="text-white font-medium">${details.current.tier_credits}</span>
                            </div>
                            <div class="flex justify-between items-center">
                                <span class="text-gray-300">${this.get_t('pricing.credits_you_get')}</span>
                                <span class="text-green-400 font-semibold">+${upgrade_calculation.final_credits - details.current.tier_credits}</span>
                            </div>
                            <div class="border-t border-gray-600/50 pt-3">
                                <div class="flex justify-between items-center">
                                    <span class="text-white font-medium">${this.get_t('pricing.final_total')}</span>
                                    <div class="text-right">
                                        <div class="text-green-400 font-bold text-lg">${upgrade_calculation.final_credits}</div>
                                        <div class="text-green-300 text-xs">${this.get_t('pricing.this_month')}</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>`;
            }

            // Billing details section
            // Payment information section - SIMPLIFIED (no more discount complexity)
            const is_annual_upgrade = details.target.billing_cycle === 'yearly';
            const following_price_key = is_annual_upgrade ? 'pricing.following_year_price' : 'pricing.following_months_price';

            html_message += `
                    <div class="space-y-4 mb-6">
                        <!-- Simple payment amount -->
                        <div class="bg-blue-900/30 border border-blue-500/30 rounded-lg p-4">
                            <div class="text-blue-200/90 text-sm">
                                <div class="mb-2 text-center">
                                    <strong>${this.get_t(following_price_key, { price: details.target.price })}</strong>
                                </div>`;

            // Only show credits info if AI system is enabled
            if (this.ai_system_enabled) {
                html_message += `
                                <div class="text-xs text-blue-200/70 text-center">
                                    ${this.get_t('pricing.credits_reset_monthly', { credits: details.target.tier_credits })}
                                </div>`;
            }

            html_message += `
                            </div>
                        </div>
                    </div>
                </div>
            `;

            this.show_confirmation_popup(
                this.get_t('pricing.upgrade_confirm_title'),
                html_message,
                () => {
                    console.log('User confirmed detailed upgrade');
                    this._proceed_with_upgrade(details.target.tier_id);
                },
                () => {
                    console.log('User cancelled detailed upgrade');
                    this.pricing_loading = false;
                }
            );
        },

        show_special_upgrade_modal(details) {
            const tier_name = details.target.base_tier === 'creator_pro' ?
                this.get_t('pricing.creator_pro.name') : this.get_t('pricing.premium.name');
            const special_info = details.special_upgrade_details;

            // Build structured HTML message
            let html_message = `
                <div class="special-upgrade-details">
                    <h3 class="text-lg font-semibold mb-4">${this.get_t('pricing.special_upgrade_header', {
                tier_name: tier_name
            })}</h3>
                    
                    <div class="space-y-4">
                        <div class="bg-blue-900/30 p-3 rounded border border-blue-600">
                            <p class="text-blue-200">${this.get_t('pricing.special_upgrade_current_line', {
                tier_name: tier_name,
                current_period_end: special_info.current_period_end
            })}</p>
                        </div>
                        
                        <div class="bg-green-900/30 p-3 rounded border border-green-600">
                            <p class="text-green-200">${this.get_t('pricing.special_upgrade_billing_line', {
                new_billing_start: special_info.new_billing_start,
                yearly_price: details.target.price
            })}</p>
                        </div>
                        
                        <div class="bg-yellow-900/30 p-3 rounded border border-yellow-600">
                            <p class="text-yellow-200 font-semibold">${this.get_t('pricing.special_upgrade_no_charge_line')}</p>
                            <p class="text-yellow-200 text-sm mt-1">${this.get_t('pricing.special_upgrade_savings_line')}</p>
                        </div>
                    </div>
                </div>
            `;

            this.show_confirmation_popup(
                this.get_t('pricing.upgrade_confirm_title'),
                html_message,
                () => {
                    console.log('User confirmed special upgrade');
                    this._proceed_with_upgrade(details.target.tier_id);
                },
                () => {
                    console.log('User cancelled special upgrade');
                    this.pricing_loading = false;
                }
            );
        },

        async show_annual_upgrade_modal(tier_id) {
            try {
                this.pricing_loading = true;

                // Get upgrade details with manual proration calculation
                const formData = new FormData();
                formData.append('request', 'get_upgrade_details');
                formData.append('tier', tier_id);

                const response = await axios.post('./subscription_api.php', formData);
                const details = response.data;

                if (!details.success) {
                    throw new Error(details.message || this.get_t('errors.upgrade_failed'));
                }

                // Use the unified modal design with annual upgrade data
                this.show_upgrade_confirmation_modal(details);

            } catch (error) {
                console.error('Error showing annual upgrade modal:', error);
                this.show_simple_confirmation(tier_id);
            } finally {
                this.pricing_loading = false;
            }
        },

        show_simple_confirmation(tier_id) {
            this.show_confirmation_popup(
                this.get_t('pricing.upgrade_confirm_title'),
                this.get_t('pricing.upgrade_proration_info'),
                () => {
                    this._proceed_with_upgrade(tier_id);
                },
                () => {
                    this.pricing_loading = false;
                }
            );
        },

        show_detailed_downgrade_modal(tier_id) {
            // Get current tier info
            const current_tier = this.current_user_tier;
            const current_billing_cycle = this.user_billing_info?.billing_cycle || 'monthly';

            // Get target tier info
            const target_base_tier = tier_id.replace('_yearly', '');
            const target_is_yearly = tier_id.includes('_yearly');
            const target_billing_cycle = target_is_yearly ? 'yearly' : 'monthly';

            // Get tier limits for pricing
            const tier_limits = this.tier_limits || {};
            const target_tier_limits = tier_limits[target_base_tier] || {};

            // Calculate target price
            let target_price = target_tier_limits.monthly_price_eur || 0;
            if (target_is_yearly) {
                const discount_months = target_tier_limits.yearly_discount_months || 0;
                target_price = target_price * (12 - discount_months);
            }

            // Get tier display names
            const current_tier_name = this.get_t(`pricing.${current_tier}.name`);
            const target_tier_name = this.get_t(`pricing.${target_base_tier}.name`);

            // Build professional downgrade modal with modern design
            const html_message = `
                <div class="downgrade-details max-w-md mx-auto">
                    <!-- Header with warning icon -->
                    <div class="text-center mb-6">
                        <div class="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-orange-500 to-red-500 rounded-2xl mb-4">
                            <svg class="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
                            </svg>
                        </div>
                        <h3 class="text-2xl font-bold text-white mb-2">${this.get_t('pricing.downgrade_confirm_title')}</h3>
                        <p class="text-gray-400 text-sm">${this.get_t('pricing.downgrade_details_subtitle')}</p>
                    </div>
                    
                    <!-- New price highlight -->
                    <div class="bg-gradient-to-r from-orange-600/20 to-red-600/20 border border-orange-500/30 rounded-xl p-6 mb-6">
                        <div class="text-center">
                            <div class="text-4xl font-bold text-white mb-2">€${target_price}</div>
                            <div class="text-orange-300 text-sm font-medium">${target_is_yearly ? this.get_t('pricing.per_year') : this.get_t('pricing.per_month')}</div>
                            <div class="text-orange-200/70 text-xs mt-1">${this.get_t('pricing.new_billing_amount')}</div>
                        </div>
                    </div>
                    
                    <!-- Plan comparison card - horizontal layout -->
                    <div class="bg-gray-800/50 border border-gray-700/50 rounded-xl p-5 mb-6">
                        <div class="flex items-center mb-4">
                            <div class="w-8 h-8 bg-orange-500/20 rounded-lg flex items-center justify-center mr-3">
                                <svg class="w-4 h-4 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"></path>
                                </svg>
                            </div>
                            <h4 class="text-lg font-semibold text-white">${this.get_t('pricing.plan_change')}</h4>
                        </div>
                        
                        <!-- Horizontal plan comparison -->
                        <div class="flex items-center justify-between">
                            <!-- Current plan -->
                            <div class="flex-1 text-center p-3 bg-blue-900/20 border border-blue-500/30 rounded-lg">
                                <div class="text-blue-300 font-medium text-sm">${this.get_t('pricing.current_plan')}</div>
                                <div class="text-blue-200 text-xs mt-1">${current_tier_name}</div>
                                <div class="text-blue-400 text-xs">(${this.get_t(`pricing.${current_billing_cycle}`)})</div>
                            </div>
                            
                            <!-- Arrow -->
                            <div class="mx-4">
                                <svg class="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
                                </svg>
                            </div>
                            
                            <!-- New plan -->
                            <div class="flex-1 text-center p-3 bg-orange-900/20 border border-orange-500/30 rounded-lg">
                                <div class="text-orange-300 font-medium text-sm">${this.get_t('pricing.new_plan')}</div>
                                <div class="text-orange-200 text-xs mt-1">${target_tier_name}</div>
                                <div class="text-orange-400 text-xs">(${this.get_t(`pricing.${target_billing_cycle}`)})</div>
                            </div>
                        </div>
                    </div>
                    
                    <!-- Important notice - compact -->
                    <div class="bg-yellow-900/20 border border-yellow-500/30 rounded-lg p-4">
                        <div class="flex items-center mb-2">
                            <div class="w-5 h-5 bg-yellow-500/20 rounded-full flex items-center justify-center mr-2">
                                <svg class="w-3 h-3 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                                </svg>
                            </div>
                            <div class="text-yellow-300 font-medium text-sm">${this.get_t('pricing.important')}</div>
                        </div>
                        <div class="text-yellow-200/80 text-sm mb-2">${this.get_t('pricing.downgrade_details.when')}</div>
                        <div class="text-yellow-200/70 text-xs">${this.get_t('pricing.downgrade_details.benefits_kept', { tier_name: current_tier_name })}</div>
                    </div>
                </div>
            `;

            this.show_confirmation_popup(
                this.get_t('pricing.downgrade_confirm_title'),
                html_message,
                () => {
                    console.log('User confirmed detailed downgrade');
                    this._proceed_with_upgrade(tier_id);
                },
                () => {
                    console.log('User cancelled detailed downgrade');
                    this.pricing_loading = false;
                }
            );
        },

        // Purchase credit pack
        async purchase_credits(pack_id) {
            if (this.pricing_loading) return;

            // Check for unsaved changes before purchase
            if (this.document_has_unsaved_changes) {
                this.show_unsaved_changes_warning(() => {
                    // Proceed with purchase after user confirms
                    this._execute_purchase_credits(pack_id);
                });
                return;
            }

            // Execute normal purchase
            this._execute_purchase_credits(pack_id);
        },

        // Internal method to execute credit purchase
        async _execute_purchase_credits(pack_id) {
            if (this.pricing_loading) return;

            console.log('Starting credit purchase for pack:', pack_id);
            this.pricing_loading = true;

            try {
                const formData = new FormData();
                formData.append('request', 'purchase_credits');
                formData.append('credit_pack', pack_id);

                const response = await axios.post('./subscription_api.php', formData);
                console.log('Credit purchase response:', response.data);

                if (response.data.success) {
                    // Redirect to Stripe Checkout
                    window.location.href = response.data.checkout_url;
                } else {
                    // Show the actual error message from server
                    const error_message = response.data.message || this.get_t('errors.purchase_failed');
                    this.show_notification_modal(error_message, 'error');
                    return;
                }

            } catch (error) {
                console.error('Credit purchase error:', error);
                // Show the actual error message to user
                const error_message = error.message || this.get_t('errors.purchase_failed');
                this.show_notification_modal(error_message, 'error');
            } finally {
                this.pricing_loading = false;
            }
        },

        // Open customer portal for subscription management
        async manage_subscription() {
            if (this.subscription_loading) return;

            this.subscription_loading = true;

            try {
                const formData = new FormData();
                formData.append('request', 'create_stripe_portal');

                const response = await axios.post('./subscription_api.php', formData);

                if (response.data.success) {
                    window.open(response.data.portal_url, '_blank');
                } else {
                    // Show the actual error message from server
                    const error_message = response.data.message || this.get_t('errors.portal_failed');
                    this.show_notification_modal(error_message, 'error');
                    return;
                }

            } catch (error) {
                console.error('Portal error:', error);
                // Show the actual error message to user
                const error_message = error.message || this.get_t('errors.portal_failed');
                this.show_notification_modal(error_message, 'error');
            } finally {
                this.subscription_loading = false;
            }
        },

        // Update user subscription status (called after successful payment)
        update_subscription_status(subscription_data) {
            // Update user_data with new subscription info
            Object.assign(this.user_data, subscription_data);
        },

        // Get tier limits using server data
        async get_tier_limits() {
            if (!this.tier_limits) {
                await this.load_pricing_config();
            }

            return this.tier_limits;
        },
        
        /**
         * Get tier limit for specific feature - CLIENT SIDE ONLY FOR DISPLAY
         */
        get_tier_limit(feature) {
            if (!this.tier_limits) return '?';
            
            const user_tier = this.user_data?.tier || 'free';
            const tier_data = this.tier_limits[user_tier] || this.tier_limits.free || {};
            
            // Map feature names to tier limit properties
            const feature_mapping = {
                'history_images': 'image_gen_history_count',
                'ai_generations': 'ai_tokens_monthly',
                'panels_per_page': 'max_panels_per_page',
                'layers_per_panel': 'max_layers_per_panel'
            };
            
            const limit_key = feature_mapping[feature] || feature;
            return tier_data[limit_key] || '?';
        },

        // Check if user can perform action based on limits
        async can_perform_action(action, current_count = 0) {
            const limits = await this.get_tier_limits();
            if (!limits) return true;

            const user_tier = this.user_data.tier;
            const tier_limits = limits[user_tier] || limits.free;

            switch (action) {
                case 'add_panel':
                    return current_count < tier_limits.max_panels_per_page;
                case 'add_balloon':
                    return current_count < tier_limits.max_balloons_per_page;
                case 'export_high_quality':
                    return this.is_premium_user;
                default:
                    return true;
            }
        },

        // Scroll to credits section
        scroll_to_credits() {
            const credits_section = document.querySelector('.bg-gradient-to-b.from-gray-800\\/30');
            if (credits_section) {
                credits_section.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start'
                });
            }
        },

        // Reset subscription data on logout
        reset_subscription_data() {
            const free_tier = this.tier_config ? this.tier_config.names.free : 'free';
            const inactive_status = this.tier_config ? this.tier_config.subscription_statuses.inactive : 'inactive';

            // Reset user_data subscription fields
            this.user_data.tier = free_tier;
            this.user_data.subscription_status = inactive_status;
            this.user_data.current_period_end = null;
            this.user_data.cancel_at_period_end = false;

            // Reset billing info
            this.user_billing_info = null;

            console.log('Subscription data reset to free tier');
        },


        // Cancel subscription completely
        async cancel_subscription() {
            if (this.subscription_loading) return;

            this.subscription_loading = true;

            try {
                const formData = new FormData();
                formData.append('request', 'cancel_subscription');

                const response = await axios.post('./subscription_api.php', formData);

                if (response.data.success) {
                    this.show_subscription_success_modal(response.data.message, 'success');
                    return { success: true, message: response.data.message };
                } else {
                    const error_message = response.data.message || this.get_t('errors.cancel_failed');
                    this.show_notification_modal(error_message, 'error');
                    return { success: false, message: error_message };
                }

            } catch (error) {
                console.error('Cancel subscription error:', error);
                const error_message = error.message || this.get_t('errors.cancel_failed');
                this.show_notification_modal(error_message, 'error');
                return { success: false, message: error_message };
            } finally {
                this.subscription_loading = false;
            }
        },

        // Cancel scheduled downgrade
        async cancel_downgrade() {
            if (this.subscription_loading) return;

            this.subscription_loading = true;

            try {
                const formData = new FormData();
                formData.append('request', 'cancel_downgrade');

                const response = await axios.post('./subscription_api.php', formData);

                if (response.data.success) {
                    this.show_subscription_success_modal(response.data.message, 'success');
                    // Reset schedule_key to default after cancelling any scheduled change
                    this.schedule_key = 'subscription.cancel_downgrade';
                    return { success: true, message: response.data.message };
                } else {
                    const error_message = response.data.message || this.get_t('errors.cancel_downgrade_failed');
                    this.show_notification_modal(error_message, 'error');
                    return { success: false, message: error_message };
                }

            } catch (error) {
                console.error('Cancel downgrade error:', error);
                const error_message = error.message || this.get_t('errors.cancel_downgrade_failed');
                this.show_notification_modal(error_message, 'error');
                return { success: false, message: error_message };
            } finally {
                this.subscription_loading = false;
            }
        },

        // Handle subscription toggle (cancel or reactivate)
        handle_subscription_toggle() {
            if (this.user_data.cancel_at_period_end) {
                // Subscription is cancelled, show reactivation confirmation
                this.show_reactivate_subscription_confirmation();
            } else {
                // Subscription is active, show cancellation confirmation
                this.show_cancel_subscription_confirmation();
            }
        },

        // Show cancel subscription confirmation
        show_cancel_subscription_confirmation() {
            // Check for unsaved changes before showing confirmation
            if (this.document_has_unsaved_changes) {
                this.show_unsaved_changes_warning(() => {
                    // Proceed with cancellation confirmation after user confirms
                    this._show_cancel_subscription_confirmation_internal();
                });
                return;
            }

            // No unsaved changes, proceed directly
            this._show_cancel_subscription_confirmation_internal();
        },

        // Internal function to show cancel subscription confirmation
        _show_cancel_subscription_confirmation_internal() {
            const confirm_message = this.get_t('subscription.cancel_subscription_confirm');

            this.show_confirmation_popup(
                this.get_t('subscription.cancel_subscription'),
                confirm_message,
                () => this.cancel_subscription(), // Confirm button - cancel subscription
                () => { } // Cancel button - do nothing, just close modal
            );
        },

        // Show reactivate subscription confirmation
        show_reactivate_subscription_confirmation() {
            // Check for unsaved changes before showing confirmation
            if (this.document_has_unsaved_changes) {
                this.show_unsaved_changes_warning(() => {
                    // Proceed with reactivation confirmation after user confirms
                    this._show_reactivate_subscription_confirmation_internal();
                });
                return;
            }

            // No unsaved changes, proceed directly
            this._show_reactivate_subscription_confirmation_internal();
        },

        // Internal function to show reactivate subscription confirmation
        _show_reactivate_subscription_confirmation_internal() {
            const confirm_message = this.get_t('subscription.reactivate_subscription_confirm');

            this.show_confirmation_popup(
                this.get_t('subscription.reactivate_subscription'),
                confirm_message,
                () => this.reactivate_subscription(), // Confirm button - reactivate subscription
                () => { } // Cancel button - do nothing, just close modal
            );
        },

        // Get button text for tier based on server data only
        get_tier_button_text(tier) {
            // Free tier is always "Incluido" and disabled
            if (tier.id === 'free') {
                return this.get_t('pricing.included');
            }

            if (tier.is_current) {
                return tier.button_text;
            }

            // Use server-calculated button text only (server now handles all scheduling logic)
            return tier.button_text;
        },

        // Toggle billing cycle between monthly and yearly
        toggle_billing_cycle() {
            this.billing_cycle = this.billing_cycle === 'monthly' ? 'yearly' : 'monthly';
            // Reload pricing config to get updated button states from server
            this.load_pricing_config();
        },

        // Get button action for tier based on current subscription state
        get_tier_button_action(tier) {
            // Check for unsaved changes before any purchase action
            if (this.document_has_unsaved_changes) {
                this.show_unsaved_changes_warning(() => {
                    // Proceed with original action after user confirms
                    this._execute_tier_button_action(tier);
                });
                return;
            }

            // Execute normal action
            this._execute_tier_button_action(tier);
        },

        // Internal method to execute tier button action
        _execute_tier_button_action(tier) {
            // Free tier never does anything
            if (tier.id === 'free') {
                return;
            }
            if (tier.is_current) {
                return; // Do nothing for current tier
            }

            // Check for special restriction (premium yearly -> creator pro monthly)
            if (tier.special_restriction) {
                this.show_special_restriction_modal();
                return;
            }

            // Normal flow - upgrade or downgrade
            if (tier.id === 'free') {
                this.show_cancel_subscription_confirmation();
            } else {
                // Use actual_id if available (for yearly plans), otherwise use tier.id
                const target_tier_id = tier.actual_id || tier.id;
                this.upgrade_to_tier(target_tier_id);
            }
        },



        // Helper function to get tier display name
        get_tier_display_name(tier_id) {
            const base_tier = tier_id.replace('_yearly', '');

            switch (base_tier) {
                case 'free':
                    return this.get_t('pricing.free.name');
                case 'premium':
                    return this.get_t('pricing.premium.name');
                case 'creator_pro':
                    return this.get_t('pricing.creator_pro.name');
                default:
                    return tier_id;
            }
        },

        // Show warning modal when user tries to change tier while having a scheduled change
        show_schedule_warning_modal(callback) {
            const downgrade_tier_name = this.get_tier_display_name(this.user_data.downgrade_tier);

            const html_message = `
                <div class="text-center">
                    <p class="text-white mb-4">${this.get_t('subscription.schedule_warning', { tier_name: downgrade_tier_name })}</p>
                    <p class="text-gray-400 text-sm">${this.get_t('subscription.schedule_warning_subtitle')}</p>
                </div>
            `;

            this.show_confirmation_popup(
                this.get_t('subscription.schedule_warning_title'),
                html_message,
                () => {
                    // User confirmed - proceed with the new action
                    callback();
                },
                () => {
                    // User cancelled - do nothing
                }
            );
        },



        // Show warning modal for unsaved changes
        show_unsaved_changes_warning(callback) {
            const html_message = `
                <div class="text-center">
                    <div class="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-orange-500 to-red-500 rounded-2xl mb-4">
                        <svg class="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
                        </svg>
                    </div>
                    <p class="text-white mb-4">${this.get_t('warnings.unsaved_changes_purchase')}</p>
                    <p class="text-gray-400 text-sm">${this.get_t('warnings.checkout_will_redirect')}</p>
                </div>
            `;

            this.show_confirmation_popup(
                this.get_t('warnings.unsaved_changes_title'),
                html_message,
                () => {
                    // User confirmed - proceed with purchase
                    callback();
                },
                () => {
                    // User cancelled - do nothing
                }
            );
        },

        // Show modal for special restriction
        show_special_restriction_modal() {
            const message = this.get_t('pricing.yearly_to_monthly_restriction');

            this.show_notification_modal(message, 'info');
            this.pricing_loading = false;
        },

        // Cancel scheduled upgrade
        async cancel_scheduled_upgrade() {
            try {
                const formData = new FormData();
                formData.append('request', 'cancel_downgrade');

                const response = await axios.post('./subscription_api.php', formData);

                if (response.data.success) {
                    const success_message = response.data.message || this.get_t('subscription.upgrade_cancelled');
                    this.show_subscription_success_modal(success_message, 'success');
                    // Hide pricing modal
                    this.hide_pricing();
                    return { success: true, message: response.data.message };
                } else {
                    const error_message = response.data.message || this.get_t('errors.cancel_upgrade_failed');
                    this.show_notification_modal(error_message, 'error');
                    return { success: false, message: error_message };
                }
            } catch (error) {
                console.error('Cancel scheduled upgrade error:', error);
                const error_message = error.message || this.get_t('errors.cancel_upgrade_failed');
                this.show_notification_modal(error_message, 'error');
                return { success: false, message: error_message };
            } finally {
                this.pricing_loading = false;
            }
        },

        // Reactivate cancelled subscription
        async reactivate_subscription() {
            if (this.subscription_loading) return;

            this.subscription_loading = true;

            try {
                const formData = new FormData();
                formData.append('request', 'reactivate_subscription');

                const response = await axios.post('./subscription_api.php', formData);

                if (response.data.success) {
                    this.show_subscription_success_modal(response.data.message, 'success');
                    return { success: true, message: response.data.message };
                } else {
                    const error_message = response.data.message || this.get_t('errors.reactivate_failed');
                    this.show_notification_modal(error_message, 'error');
                    return { success: false, message: error_message };
                }

            } catch (error) {
                console.error('Reactivate subscription error:', error);
                const error_message = error.message || this.get_t('errors.reactivate_failed');
                this.show_notification_modal(error_message, 'error');
                return { success: false, message: error_message };
            } finally {
                this.subscription_loading = false;
            }
        },

        // Handle scheduled change cancellation from top menu (determines correct function based on schedule_key)
        async handle_scheduled_change_cancellation() {
            // Check for unsaved changes before proceeding
            if (this.document_has_unsaved_changes) {
                this.show_unsaved_changes_warning(() => {
                    // Proceed with scheduled change cancellation after user confirms
                    this._handle_scheduled_change_cancellation_internal();
                });
                return;
            }

            // No unsaved changes, proceed directly
            this._handle_scheduled_change_cancellation_internal();
        },

        // Internal function to handle scheduled change cancellation
        async _handle_scheduled_change_cancellation_internal() {
            if (this.schedule_key === 'subscription.cancel_upgrade') {
                await this.cancel_scheduled_upgrade();
            } else {
                await this.cancel_downgrade();
            }
        },

        // Handle page refresh after critical subscription actions
        handle_page_refresh() {
            window.location.reload();
        },
    },

    watch: {
        // Watch for user_data changes to trigger reactive updates
        user_data: {
            handler(new_data, old_data) {
                if (!new_data && old_data) {
                    // User logged out, reset to free
                    this.reset_subscription_data();
                }
                // Force reactive update when user data changes
                this.$nextTick(() => {
                    this.$forceUpdate();
                });
            },
            deep: true,
            immediate: true
        }
    }
};
